import { videos, categories } from "@/mocks/videos";
import SEO from "@/components/base/SEO";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import HeroSection from "./components/HeroSection";
import CategoryNav from "./components/CategoryNav";
import VideoCard from "@/components/feature/VideoCard";
import StatsSection from "./components/StatsSection";

export default function Home() {
  const featuredVideos = videos.filter((v) => v.featured);
  const hotVideos = videos.filter((v) => v.hot);
  const latestVideos = [...videos].sort((a, b) => b.date.localeCompare(a.date));

  const getCategoryName = (slug: string) => {
    return categories.find((c) => c.slug === slug)?.name || slug;
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "久久视频是免费的吗？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "是的，久久视频平台上的所有内容都是免费在线观看的，无需注册或付费。我们致力于为用户提供最优质的免费视频内容服务。",
        },
      },
      {
        "@type": "Question",
        name: "久久视频包含哪些类型的视频内容？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "久久视频聚合了蜜桃视频、樱桃视频、蘑菇视频、茄子视频等多个平台的泛娱乐内容，涵盖综艺节目、动漫番剧、短视频、搞笑创意视频等多种类型。",
        },
      },
    ],
  };

  return (
    <>
      <SEO schema={faqSchema} />
      <Navbar />

      <main>
        <HeroSection />

        <CategoryNav />

        <section className="w-full bg-background-50 py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900">
                精选推荐
              </h2>
              <a href="/category/gaoxiao" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer transition-colors flex items-center gap-1 whitespace-nowrap">
                查看更多 <i className="ri-arrow-right-line"></i>
              </a>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {featuredVideos.map((video) => (
                <VideoCard
                  key={video.id}
                  id={video.id}
                  title={video.title}
                  thumbnail={video.thumbnail}
                  duration={video.duration}
                  views={video.views}
                  date={video.date}
                  category={video.category}
                  categoryName={getCategoryName(video.category)}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100/50 py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900">
                热门视频
              </h2>
              <a href="/category/duanshipin" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer transition-colors flex items-center gap-1 whitespace-nowrap">
                查看更多 <i className="ri-arrow-right-line"></i>
              </a>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {hotVideos.map((video) => (
                <VideoCard
                  key={video.id}
                  id={video.id}
                  title={video.title}
                  thumbnail={video.thumbnail}
                  duration={video.duration}
                  views={video.views}
                  date={video.date}
                  category={video.category}
                  categoryName={getCategoryName(video.category)}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900">
                最新上线
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {latestVideos.slice(0, 8).map((video) => (
                <VideoCard
                  key={video.id}
                  id={video.id}
                  title={video.title}
                  thumbnail={video.thumbnail}
                  duration={video.duration}
                  views={video.views}
                  date={video.date}
                  category={video.category}
                  categoryName={getCategoryName(video.category)}
                />
              ))}
            </div>
          </div>
        </section>

        <StatsSection />

        <section className="w-full bg-background-100/50 py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 text-center mb-8">
              常见问题
            </h2>
            <div className="space-y-3">
              {[
                {
                  q: "久久视频是免费的吗？",
                  a: "是的，久久视频平台上的所有内容都是免费在线观看的，无需注册或付费。我们致力于为用户提供最优质的免费视频内容服务。",
                },
                {
                  q: "久久视频包含哪些类型的视频内容？",
                  a: "久久视频聚合了蜜桃视频、樱桃视频、蘑菇视频、茄子视频等多个平台的泛娱乐内容，涵盖综艺节目、动漫番剧、短视频、搞笑创意视频等多种类型，满足不同用户的观看需求。",
                },
                {
                  q: "如何在久久视频上找到我想看的内容？",
                  a: "您可以通过首页的分类导航快速筛选感兴趣的视频类型，也可以使用搜索功能查找特定内容。我们还设有热门推荐和最新上线板块，帮助您发现更多精彩视频。",
                },
                {
                  q: "久久视频支持移动端观看吗？",
                  a: "当然支持！久久视频网站完美适配手机、平板和电脑等多种设备，无论您使用什么设备，都能获得流畅的观看体验。",
                },
              ].map((faq, i) => (
                <details
                  key={i}
                  className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden cursor-pointer"
                >
                  <summary className="flex items-center justify-between px-5 py-4 text-sm font-medium text-foreground-900 list-none">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}